<template>
  <div class="page-container">
    <MainTitle title="New Conversation" />
    <div class="app-content-container">
      <Toast />
      <div class="subject-input-wrapper">
        <Button
          text
          icon="pi pi-arrow-left"
          label="Back"
          class="back-btn"
          @click="$router.back()"
        />

        <h2 class="form-heading">
          What would you like to talk about?
        </h2>

        <div
          v-if="hasNoSubjects"
          class="no-subjects-banner"
        >
          <i class="pi pi-info-circle no-subjects-icon" />
          <span>
            No subjects created yet. Please
            <router-link
              :to="{ name: 'SubjectsList' }"
              class="no-subjects-link"
            >create a subject</router-link>
            before starting a new chat.
          </span>
        </div>

        <div class="input-row">
          <AutoComplete
            v-model="topic"
            dropdown
            :suggestions="filteredSubjects"
            placeholder="e.g. &quot;Ordering food in Tokyo&quot;"
            class="topic-input"
            :invalid="topicError"
            :disabled="hasNoSubjects"
            option-label="display_name"
            data-test="subject-select"
            @complete="filterSubjects"
            @item-select="onSubjectSelect"
            @keyup.enter="handleGenerate"
          />
          <Button
            :label="isGenerating ? 'Generating scenarios…' : 'Generate'"
            data-test="generate-scenarios-btn"
            class="ns-btn-primary"
            :loading="isGenerating"
            :disabled="hasNoSubjects"
            @click="handleGenerate"
          >
            <template #icon>
              <font-awesome-icon
                :icon="['fas', 'paper-plane']"
                class="p-button-icon p-button-icon-left"
              />
            </template>
          </Button>
        </div>
        <small
          v-if="topicError"
          class="error-text"
        >
          {{ SUBJECT_EMPTY_ERROR_MSG }}
        </small>

        <!-- Scenario Selection Section -->
        <div
          v-if="scenarios.length"
          class="scenarios-section"
        >
          <h3 class="scenarios-heading">
            Choose a Scenario
          </h3>

          <div class="scenarios-grid">
            <Card
              v-for="scenario in scenarios"
              :key="scenario.id"
              class="scenario-card"
              @click="selectScenario(scenario)"
            >
              <template #title>
                <div class="scenario-card-header">
                  <Tag
                    :value="getDifficultyName(scenario.difficulty_level)"
                    :severity="difficultySeverity(scenario.difficulty_level)"
                    class="difficulty-tag"
                  />
                </div>
                <div class="scenario-title">
                  {{ scenario.title }}
                </div>
              </template>
              <template #content>
                <div
                  v-if="scenario.character_name"
                  class="character-row"
                >
                  <i class="pi pi-user character-icon" />
                  <span class="character-name">{{ scenario.character_name }}</span>
                </div>
                <p class="scenario-description">
                  {{ scenario.description }}
                </p>
              </template>
              <template #footer>
                <Button
                  label="Start Chat"
                  class="ns-btn-primary start-chat-btn"
                  :loading="startingScenarioId === scenario.id"
                  :disabled="startingScenarioId !== null"
                  data-test="start-chat-btn"
                  @click.stop="selectScenario(scenario)"
                >
                  <template #icon>
                    <font-awesome-icon
                      :icon="['fas', 'comment']"
                      class="p-button-icon p-button-icon-left"
                    />
                  </template>
                </Button>
              </template>
            </Card>
          </div>

          <div class="regenerate-row">
            <Button
              icon="pi pi-refresh"
              :label="isRegenerating ? 'Generating…' : 'Generate new scenarios'"
              class="ns-btn-secondary"
              :loading="isRegenerating"
              @click="regenerate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainTitle from '@/components/title/MainTitle.vue'
import Button from 'primevue/button'
import AutoComplete from 'primevue/autocomplete'
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Toast from 'primevue/toast'
import { getWithAuthentication } from '@/services/api'
import { connectAndCreateSession, warmUpLambda } from '@/services/chatSessionService'
import { 
  BASE_URL, 
  LIBRARY_SUBJECT_ENDPOINT 
} from '@/assets/constants/api_endpoints'
import { 
  SUBJECT_EMPTY_ERROR_MSG, 
  GENERATE_SCENARIOS_ERROR_MSG,
  DIFFICULTY_SEVERITY,
  DIFFICULTY_LEVEL_MAP,
  JAPANESE_LANGUAGE_CODE,
  CHAT_SESSION_START_ERROR_MSG,
  CHAT_WEEKLY_LIMIT_MSG,
  CHAT_WEEKLY_LIMIT_UI_MSG,
  CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT,
  FAILED_STATUS,
} from '@/assets/constants/constants'
import StorageService from '@/services/StorageService'

export default {
  name: 'ChatSubjectInput',
  components: { MainTitle, Button, AutoComplete, Card, Tag, Toast },
  data() {
    return {
      topic: '',
      topicError: false,
      isGenerating: false,
      isRegenerating: false,
      startingScenarioId: null,
      subjectList: [],
      filteredSubjects: [],
      subjectsLoaded: false,
      SUBJECT_EMPTY_ERROR_MSG
    }
  },
  computed: {
    scenarios() {
      return this.$store.getters['chat/scenarios']
    },
    hasNoSubjects() {
      return this.subjectsLoaded && this.subjectList.length === 0
    }
  },
  async mounted() {
    // Clear scenarios when entering this page
    await this.$store.dispatch('chat/clearChat')
    await this.loadSubjects()
  },
  methods: {
    async loadSubjects() {
      try {
        const response = await getWithAuthentication(BASE_URL, LIBRARY_SUBJECT_ENDPOINT)
        if (response?.status === 200) {
          this.subjectList = response.data
            .filter(s => !s.is_deleted && !s.is_sample)
            .map(s => ({
              ...s,
              display_name: s.subject_name,
              id: s.id
            }))
          // Initialize filtered subjects with all subjects
          this.filteredSubjects = this.subjectList
        }
      } catch {
        // Non-critical: subject suggestions are a convenience feature only
      } finally {
        this.subjectsLoaded = true
      }
    },
    filterSubjects(event) {
      const query = event.query.toLowerCase()
      if (!query) {
        this.filteredSubjects = [...this.subjectList]
      } else {
        this.filteredSubjects = this.subjectList.filter(subject =>
          subject.display_name.toLowerCase().includes(query)
        )
      }
    },
    onSubjectSelect(event) {
      const selectedSubject = event.value
      this.topic = selectedSubject.display_name
      this.topicError = false
      // Store the subject with ID for scenario generation
      this.$store.dispatch(
        'chat/setCurrentSubject', 
        { 
          id: selectedSubject.id, 
          name: selectedSubject.display_name
        }
      )
    },
    async handleGenerate() {
      this.topicError = false
      if (!this.topic.trim()) {
        this.topicError = true
        return
      }
      this.isGenerating = true
      try {
        // Ensure we have a subject ID (from selection or manual input)
        if (!this.$store.state.chat.currentSubject?.id) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Please select a subject from the dropdown',
            detail: 'Free-text topic entry will be available in the next phase.',
            life: 4000
          })
          this.isGenerating = false
          return
        }
        // Fire-and-forget: warm up Lambda while user browses scenarios
        warmUpLambda()
        await this.$store.dispatch('chat/fetchScenarios')
      } catch {
        this.$toast.add({ severity: FAILED_STATUS, summary: GENERATE_SCENARIOS_ERROR_MSG, life: 4000 })
      } finally {
        this.isGenerating = false
      }
    },
    getDifficultyName(difficultyLevel) {
      return DIFFICULTY_LEVEL_MAP[difficultyLevel] || 'Unknown'
    },
    difficultySeverity(difficultyLevel) {
      const difficultyName = DIFFICULTY_LEVEL_MAP[difficultyLevel]
      return DIFFICULTY_SEVERITY[difficultyName] ?? 'info'
    },
    selectScenario(scenario) {
      this.startChatSession(scenario)
    },
    async startChatSession(scenario) {
      this.startingScenarioId = scenario.id
      try {
        console.log('[ChatSubjectInput] Starting chat session for scenario:', scenario.id)
        
        const token = StorageService.getAuthToken()
        if (!token) {
          throw new Error('No authentication token found')
        }
        
        await new Promise(resolve => setTimeout(resolve, 3000))

        console.log('[ChatSubjectInput] Connecting to WebSocket...')
        // Connect to the WebSocket endpoint and wait for session creation
        const session = await connectAndCreateSession(scenario.id, token)
        console.log('[ChatSubjectInput] Session created:', session)
        
        // Store the active session in Vuex
        const currentSubject = this.$store.state.chat.currentSubject
        this.$store.commit('chat/SET_ACTIVE_SESSION', {
          id: session.session_id,
          subject_id: currentSubject?.id,
          scenario_id: scenario.id,
          scenario_title: scenario.title,
          scenario_emoji: scenario.emoji || '',
          created_at: new Date().toISOString()
        })
        
        console.log('[ChatSubjectInput] Navigating to chat session...')
        // Navigate to chat session page
        this.$router.push({
          name: 'ChatSession',
          params: { sessionId: session.session_id, subject: currentSubject?.id }
        })
      } catch (error) {
        console.error('[ChatSubjectInput] Failed to start chat session:', error)
        const isWeeklyLimitError =
          error?.code === CHAT_WS_CLOSE_CODE_WEEKLY_LIMIT ||
          error?.message === CHAT_WEEKLY_LIMIT_MSG

        this.$toast.add({
          severity: FAILED_STATUS,
          summary: isWeeklyLimitError ? 'Weekly limit reached' : CHAT_SESSION_START_ERROR_MSG,
          detail: isWeeklyLimitError ? CHAT_WEEKLY_LIMIT_UI_MSG : error.message,
          life: 4000
        })
      } finally {
        this.startingScenarioId = null
      }
    },
    async regenerate() {
      this.isRegenerating = true
      try {
        await this.$store.dispatch('chat/fetchScenarios', { language: JAPANESE_LANGUAGE_CODE, forceRegenerate: true })
      } catch {
        this.$toast.add({ severity: FAILED_STATUS, summary: GENERATE_SCENARIOS_ERROR_MSG, life: 4000 })
      } finally {
        this.isRegenerating = false
      }
    }
  }
}
</script>

<style scoped>
.subject-input-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1000px;
  padding: 8px 0;
}

.back-btn {
  margin-bottom: 16px;
  padding-left: 0 !important;
}

.form-heading {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin-bottom: var(--space-5);
}

.input-row {
  display: flex;
  gap: 12px;
  width: 100%;
  align-items: center;
}

.topic-input {
  flex: 1;
}

.error-text {
  color: var(--color-error);
  margin-top: var(--space-1);
  font-size: var(--font-size-sm);
}

.no-subjects-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  margin-bottom: var(--space-4);
  background: rgba(27, 30, 100, 0.05);
  border: 1px solid rgba(27, 30, 100, 0.15);
  border-radius: var(--radius-md);
  color: var(--color-primary);
  font-size: var(--font-size-sm);
}

.no-subjects-icon {
  flex-shrink: 0;
  font-size: 16px;
}

.no-subjects-link {
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
  text-decoration: underline;
}

.scenarios-section {
  width: 100%;
  margin-top: 40px;
}

.scenarios-heading {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin-bottom: var(--space-5);
}

.scenarios-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  width: 100%;
  margin-bottom: 20px;
}

.scenario-card {
  flex: 0 1 calc(50% - 10px);
  min-width: 260px;
  cursor: pointer;
  background: var(--color-surface) !important;
  border: 1px solid rgba(27,30,100,0.10);
  border-radius: var(--radius-lg) !important;
  box-shadow: none !important;
  transition: border-color var(--transition-base);
  padding: 10px;
}

.scenario-card:hover {
  box-shadow: none !important;
  border-color: var(--color-primary-mid) !important;
}

.scenario-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.difficulty-tag {
  font-size: 0.75rem;
}

.scenario-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin-top: var(--space-1);
}

.character-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.character-icon {
  font-size: var(--font-size-xs);
  color: var(--color-primary-mid);
}

.character-name {
  font-size: var(--font-size-xs);
  color: var(--color-primary-mid);
  font-weight: var(--font-weight-medium);
}

.scenario-description {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: var(--line-height-base);
}

.start-chat-btn {
  width: 100%;
  justify-content: center;
}

.regenerate-row {
  width: 100%;
  text-align: center;
}

@media (max-width: 600px) {
  .input-row {
    flex-direction: column;
    align-items: stretch;
  }

  .scenarios-grid {
    grid-template-columns: 1fr;
    justify-items: center;
  }

  .scenario-card {
    width: 100%;
    max-width: 340px;
  }
}
</style>
